import * as stylex from "@stylexjs/stylex";
import React from "react";
import { defineMessage, useIntl } from "react-intl";
import ReactSelect, { components } from "react-select";
import { useRTL } from "../hooks";
import * as M from "../materials";
import { typography } from "../materials/typography.stylex";
const styles = {
    title: {
        k1K539: "x12nagc",
        $$css: true
    }
};
export const MultiSelect = ({ items: itemsRaw, selectedItems: selectedItemsRaw, label, onChange, minItems = 1, maxItems = Infinity, isInline = true, title })=>{
    const intl = useIntl();
    const isRTL = useRTL();
    const items = React.useMemo(()=>{
        return itemsRaw.map((item)=>({
                ...item,
                isFixed: false
            }));
    }, [
        itemsRaw
    ]);
    const selectedItems = React.useMemo(()=>{
        return selectedItemsRaw.map((x)=>items.find((y)=>y.value === x));
    }, [
        items,
        selectedItemsRaw
    ]);
    // Using mutation internally, to work around overly aggressive ReactSelect caching
    for (const item of items){
        item.isFixed = selectedItems.length <= minItems && selectedItemsRaw.includes(item.value);
    }
    return <>
      {title && <h4 {...stylex.props(typography.heading4, styles.title)}>{title}</h4>}
      <ReactSelect styles={customStyles(isInline)} value={selectedItems} options={selectedItems.length >= maxItems ? maxItemsMessage(intl.formatMessage(defineMessage({
        id: "Remove an item before adding another one",
        defaultMessage: "Remove an item before adding another one"
    }))) : items} onChange={React.useCallback((value, actionMeta)=>{
        switch(actionMeta.action){
            case "remove-value":
            case "pop-value":
                if (actionMeta.removedValue?.isFixed) {
                    return;
                }
                break;
        }
        onChange(value, actionMeta);
    }, [
        onChange
    ])} isMulti isRtl={isRTL} isClearable={false} placeholder={label} noOptionsMessage={()=>intl.formatMessage(defineMessage({
            id: "No matching items found",
            defaultMessage: "No matching items found"
        }))} components={{
        DropdownIndicator
    }} data-control-type="select"/>
    </>;
};
// -----------------------------------------------------------------------------
const maxItemsMessage = (label)=>[
        {
            label,
            value: "",
            isDisabled: true
        }
    ];
// -----------------------------------------------------------------------------
const customStyles = (isInline)=>({
        control: (provided, state)=>{
            return {
                ...provided,
                width: "100%",
                background: M.whiteText,
                border: `1px solid ${M.grayscalePalette[4]}`,
                borderRadius: 4,
                minHeight: "32px",
                minWidth: 160,
                justifyContent: "space-between",
                marginBottom: 8,
                // This is the valueContainer; we need to style it here to get access to
                // the "isFocused" property, which unfortunately is not passed down.
                // Only used for show/hide styles, other styles are in "valueContainer".
                "& > *": {
                    maxHeight: state.isFocused || !isInline ? "auto" : 30 /* "32px" - 2px border */ ,
                    "& > *": {
                        fontSize: state.isFocused ? "1em" : "0.8em",
                        marginTop: state.isFocused || !isInline ? "" : "0!important",
                        marginBottom: state.isFocused || !isInline ? "" : "0!important"
                    }
                }
            };
        },
        input: (provided)=>({
                ...provided,
                ...M.fontTable[0],
                marginTop: 0,
                marginBottom: 0,
                color: M.blackText
            }),
        valueContainer: (provided)=>({
                ...provided,
                paddingLeft: 2,
                paddingRight: 0
            }),
        multiValue: (provided)=>({
                ...provided,
                margin: "2px",
                background: M.grayscalePalette[3]
            }),
        multiValueLabel: (provided)=>({
                ...provided,
                color: M.unescoMarineBlue,
                padding: "2px"
            }),
        multiValueRemove: (provided, state)=>{
            return state.data.isFixed ? {
                ...provided,
                display: "none"
            } : {
                ...provided,
                color: M.unescoMarineBlue,
                "&:hover": {
                    color: M.q03Palette[7],
                    background: M.grayscalePalette[3],
                    cursor: "pointer"
                }
            };
        },
        clearIndicator: (provided)=>({
                ...provided,
                paddingTop: 4,
                paddingBottom: 4,
                color: M.grayscalePalette[5],
                "&:hover": {
                    color: M.grayscalePalette[6],
                    cursor: "pointer"
                }
            }),
        dropdownIndicator: (provided)=>({
                ...provided,
                padding: "0 12px"
            }),
        indicatorSeparator: ()=>({
                display: "none"
            }),
        placeholder: (provided)=>({
                ...provided,
                ...M.fontTable[0],
                color: M.unescoMarineBlue
            }),
        menu: (provided)=>({
                ...provided,
                background: M.grayscalePalette[2],
                marginTop: 2,
                boxShadow: "none"
            }),
        menuList: (provided)=>({
                ...provided,
                border: `1px solid ${M.grayscalePalette[4]}`,
                borderRadius: 4
            }),
        option: (provided, state)=>({
                ...provided,
                ...M.fontTable[0],
                padding: "6px 8px",
                background: state.isFocused ? M.grayscalePalette[3] : M.grayscalePalette[2],
                color: state.isFocused ? M.unescoMarineBlue : M.blackText,
                margin: "0 4px",
                width: "calc(100% - 8px)",
                borderRadius: 3
            }),
        noOptionsMessage: (provided)=>({
                ...provided,
                ...M.fontTable[0],
                color: M.lightText
            })
    });
// -----------------------------------------------------------------------------
function DropdownIndicator(props) {
    return <components.DropdownIndicator {...props}>
      <svg width="10px" height="6px" viewBox="0 0 10 6">
        <polygon points="0 0 5 6 10 0" fill={M.unescoMarineBlue}/>
      </svg>
    </components.DropdownIndicator>;
}
